import { useEffect, useState } from "react";
import { reservasAdminApi, reservasApi, api } from "../lib/api";

interface Sucursal { id: number; codigo: string; nombre: string; }
interface Cliente {
  codCliente: number; nombreCliente: string;
  email: string | null; telefono1: string | null; mobil: string | null; cif: string | null;
}
interface RecursoInfo { id: number; nombre: string; capacidad: number; horaInicio?: string | null; horaFin?: string | null; }
interface ZonaInfo { id: number; nombre: string; permiteSeleccionMesa?: boolean; recursos?: RecursoInfo[]; bloquesTiempoMinutos?: number | null; }

function generarSlots(): string[] {
  const s: string[] = [];
  for (let h = 7; h <= 23; h++) { s.push(`${String(h).padStart(2,"0")}:00`); s.push(`${String(h).padStart(2,"0")}:30`); }
  return s;
}
function hoyISO() { return new Date().toISOString().slice(0, 10); }

export function NuevaReservaModal({ onClose, onCreated, fechaInicial }: { onClose: () => void; onCreated: () => void; fechaInicial?: string }) {
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [zonas, setZonas] = useState<ZonaInfo[]>([]);
  const [sucursalId, setSucursalId] = useState<number | null>(null);

  // Cliente
  const [busqueda, setBusqueda] = useState("");
  const [resultados, setResultados] = useState<Cliente[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [clienteSel, setClienteSel] = useState<Cliente | null>(null);
  const [modoCrear, setModoCrear] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState({ nombreCliente: "", email: "", telefono1: "", cif: "" });

  // Reserva
  const [sucConfig, setSucConfig] = useState<any>(null);
  const [form, setForm] = useState({
    fechaReserva: fechaInicial ?? hoyISO(), horaReserva: "20:00", numPersonas: 2,
    zonaId: undefined as number | undefined,
    recursoId: undefined as number | undefined,
    duracionMinutos: undefined as number | undefined,
    requerimientosEspeciales: "", notasInternas: "",
    confirmarInmediatamente: false,
  });

  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    reservasAdminApi.sucursales().then((s) => {
      setSucursales(s);
      if (s.length > 0) {
        setSucursalId(s[0].id);
        // Cargar config de la primera sucursal (duración, etc.)
        fetch(`/api/v1/sucursales/${s[0].id}/config`)
          .then(r => r.json()).then(setSucConfig).catch(() => {});
      }
    }).catch(() => {});
    reservasApi.reservarInfo?.().then((info: any) => {
      if (info?.zonas) setZonas(info.zonas);
    }).catch(() => {});
  }, []);

  // Buscar clientes (debounce simple)
  useEffect(() => {
    if (busqueda.trim().length < 3) { setResultados([]); return; }
    setBuscando(true);
    const t = setTimeout(() => {
      reservasAdminApi.buscarCliente(busqueda.trim())
        .then(setResultados)
        .catch(() => setResultados([]))
        .finally(() => setBuscando(false));
    }, 350);
    return () => clearTimeout(t);
  }, [busqueda]);

  function seleccionarCliente(c: Cliente) {
    setClienteSel(c);
    setModoCrear(false);
    setResultados([]);
    setBusqueda("");
  }

  async function crearClienteYSeguir() {
    setError(null);
    if (!nuevoCliente.nombreCliente.trim()) { setError("El nombre del cliente es obligatorio"); return; }
    if (!nuevoCliente.email && !nuevoCliente.telefono1 && !nuevoCliente.cif) {
      setError("Ingresá al menos email, teléfono o documento"); return;
    }
    setGuardando(true);
    try {
      const r = await reservasAdminApi.crearCliente({
        nombreCliente: nuevoCliente.nombreCliente.trim(),
        email: nuevoCliente.email.trim() || undefined,
        telefono1: nuevoCliente.telefono1.trim() || undefined,
        cif: nuevoCliente.cif.trim() || undefined,
      });
      // Sea creado nuevo o ya existente, lo seleccionamos
      setClienteSel({
        codCliente: r.codCliente, nombreCliente: r.nombreCliente,
        email: nuevoCliente.email || null, telefono1: nuevoCliente.telefono1 || null,
        mobil: null, cif: nuevoCliente.cif || null,
      });
      setModoCrear(false);
      if (r.yaExistia) setError(`Aviso: ${r.mensaje}. Se usará el cliente existente.`);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  }

  async function crearReserva() {
    setError(null);
    if (!sucursalId) { setError("Elegí un restaurante"); return; }
    if (!clienteSel) { setError("Buscá o creá un cliente"); return; }
    const hoy = new Date(); hoy.setHours(0,0,0,0);
    if (new Date(form.fechaReserva + "T00:00:00") < hoy) { setError("La fecha no puede ser pasada"); return; }

    setGuardando(true);
    try {
      const reserva = await reservasAdminApi.crearReserva({
        codCliente: clienteSel.codCliente,
        sucursalId,
        nombreCliente: clienteSel.nombreCliente,
        telefono: clienteSel.telefono1 ?? clienteSel.mobil ?? undefined,
        email: clienteSel.email ?? undefined,
        fechaReserva: form.fechaReserva,
        horaReserva: form.horaReserva,
        numPersonas: form.numPersonas,
        zonaId: form.zonaId,
        recursoId: form.recursoId,
        duracionMinutos: form.duracionMinutos,
        requerimientosEspeciales: form.requerimientosEspeciales.trim() || undefined,
        notasInternas: form.notasInternas.trim() || undefined,
      });
      // Confirmar inmediatamente si se seleccionó
      if (form.confirmarInmediatamente && (reserva as any)?.id) {
        await reservasApi.confirmar((reserva as any).id).catch(() => {});
      }
      onCreated();
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Nueva reserva</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl">✕</button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="p-2.5 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded">{error}</div>
          )}

          {/* Sucursal */}
          <div>
            <label className="block text-xs text-slate-500 mb-1.5">Restaurante</label>
            <div className="flex gap-2">
              {sucursales.map((s) => (
                <button key={s.id} onClick={() => setSucursalId(s.id)}
                  className={`px-4 py-2 rounded-md text-sm border ${sucursalId === s.id ? "bg-brand-600 text-white border-brand-600" : "bg-white text-slate-700 border-slate-300"}`}>
                  {s.nombre}
                </button>
              ))}
            </div>
          </div>

          {/* Cliente */}
          <div className="border-t border-slate-100 pt-4">
            <label className="block text-xs text-slate-500 mb-1.5">Cliente</label>

            {clienteSel ? (
              <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                <div className="text-sm">
                  <div className="font-medium text-slate-900">{clienteSel.nombreCliente}</div>
                  <div className="text-xs text-slate-500">
                    #{clienteSel.codCliente}
                    {clienteSel.telefono1 && ` · ${clienteSel.telefono1}`}
                    {clienteSel.email && ` · ${clienteSel.email}`}
                  </div>
                </div>
                <button onClick={() => { setClienteSel(null); }} className="text-xs text-slate-500 hover:text-slate-700">Cambiar</button>
              </div>
            ) : modoCrear ? (
              <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-md p-3">
                <input placeholder="Nombre completo *" value={nuevoCliente.nombreCliente}
                  onChange={(e) => setNuevoCliente({ ...nuevoCliente, nombreCliente: e.target.value })}
                  className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm" />
                <input placeholder="Teléfono" value={nuevoCliente.telefono1}
                  onChange={(e) => setNuevoCliente({ ...nuevoCliente, telefono1: e.target.value })}
                  className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm" />
                <input placeholder="Email" value={nuevoCliente.email}
                  onChange={(e) => setNuevoCliente({ ...nuevoCliente, email: e.target.value })}
                  className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm" />
                <input placeholder="Documento de identidad" value={nuevoCliente.cif}
                  onChange={(e) => setNuevoCliente({ ...nuevoCliente, cif: e.target.value })}
                  className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm" />
                <div className="text-xs text-slate-400">Requerido: nombre + (teléfono, email o documento)</div>
                <div className="flex gap-2">
                  <button onClick={crearClienteYSeguir} disabled={guardando}
                    className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded hover:bg-emerald-700 disabled:opacity-40">
                    {guardando ? "Guardando…" : "Guardar cliente"}
                  </button>
                  <button onClick={() => setModoCrear(false)}
                    className="px-3 py-1.5 border border-slate-300 text-slate-700 text-sm rounded">Cancelar</button>
                </div>
              </div>
            ) : (
              <div>
                <input
                  value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar por teléfono, email o documento…"
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
                {buscando && <div className="text-xs text-slate-400 mt-1">Buscando…</div>}
                {resultados.length > 0 && (
                  <div className="mt-2 border border-slate-200 rounded-md divide-y max-h-48 overflow-y-auto">
                    {resultados.map((c) => (
                      <button key={c.codCliente} onClick={() => seleccionarCliente(c)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                        <div className="font-medium text-slate-900">{c.nombreCliente}</div>
                        <div className="text-xs text-slate-500">
                          #{c.codCliente}{c.telefono1 && ` · ${c.telefono1}`}{c.email && ` · ${c.email}`}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {busqueda.trim().length >= 3 && !buscando && resultados.length === 0 && (
                  <div className="text-xs text-slate-500 mt-2">No se encontró. </div>
                )}
                <button onClick={() => { setModoCrear(true); setError(null); }}
                  className="mt-2 text-sm text-brand-700 hover:underline">+ Crear cliente nuevo</button>
              </div>
            )}
          </div>

          {/* Datos de reserva (solo si hay cliente y sucursal) */}
          {clienteSel && sucursalId && (
            <div className="border-t border-slate-100 pt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Fecha</label>
                  <input type="date" min={hoyISO()} value={form.fechaReserva}
                    onChange={(e) => setForm({ ...form, fechaReserva: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm" />
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Hora</label>
                  <select value={form.horaReserva} onChange={(e) => setForm({ ...form, horaReserva: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm">
                    {generarSlots().map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Personas</label>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setForm({ ...form, numPersonas: Math.max(1, form.numPersonas - 1) })}
                      className="w-9 h-9 border border-slate-300 rounded text-lg">−</button>
                    <span className="text-lg w-8 text-center">{form.numPersonas}</span>
                    <button onClick={() => setForm({ ...form, numPersonas: Math.min(50, form.numPersonas + 1) })}
                      className="w-9 h-9 border border-slate-300 rounded text-lg">+</button>
                  </div>
                </div>
                {zonas.length > 0 && (
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Zona</label>
                    <select value={form.zonaId ?? ""} onChange={(e) => setForm({ ...form, zonaId: e.target.value ? Number(e.target.value) : undefined, recursoId: undefined })}
                      className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm">
                      <option value="">Sin preferencia</option>
                      {zonas.map((z) => <option key={z.id} value={z.id}>{z.nombre}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Selector de mesa/pista cuando la zona lo permite */}
              {(() => {
                const zonaObj = zonas.find(z => z.id === form.zonaId);
                const recursos = zonaObj?.recursos ?? [];
                if (!zonaObj?.permiteSeleccionMesa || recursos.length === 0) return null;
                const label = zonaObj.nombre.toLowerCase().includes('bowl') ? 'Pista' : 'Mesa';
                return (
                  <div>
                    <label className="block text-xs text-slate-500 mb-1.5">
                      {label} específica <span className="text-slate-400">(opcional)</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {recursos.map(r => (
                        <button key={r.id} type="button"
                          onClick={() => setForm({ ...form, recursoId: form.recursoId === r.id ? undefined : r.id })}
                          className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${form.recursoId === r.id ? "bg-brand-600 text-white border-brand-600" : "bg-white text-slate-700 border-slate-300 hover:border-brand-400"}`}>
                          {r.nombre}
                          {r.capacidad > 0 && <span className="text-xs opacity-60 ml-1">{r.capacidad}p</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Duración (bowling, padel, etc.) */}
              {sucConfig?.usaDuracionReserva && (sucConfig.duracionOpciones ?? []).length > 0 && (
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5">Duración</label>
                  <div className="flex flex-wrap gap-2">
                    {(sucConfig.duracionOpciones as number[]).map(min => (
                      <button key={min} type="button"
                        onClick={() => setForm({ ...form, duracionMinutos: form.duracionMinutos === min ? undefined : min })}
                        className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${form.duracionMinutos === min ? "bg-brand-600 text-white border-brand-600" : "bg-white text-slate-700 border-slate-300 hover:border-brand-400"}`}>
                        {min >= 60 ? `${Math.floor(min/60)}h${min%60 ? ` ${min%60}min` : ""}` : `${min}min`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs text-slate-500 mb-1">Requerimientos especiales</label>
                <textarea rows={2} value={form.requerimientosEspeciales}
                  onChange={(e) => setForm({ ...form, requerimientosEspeciales: e.target.value })}
                  className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm"
                  placeholder="Alergias, celebración, etc. (opcional)" />
              </div>

              <div>
                <label className="block text-xs text-slate-500 mb-1">Notas internas</label>
                <textarea rows={2} value={form.notasInternas}
                  onChange={(e) => setForm({ ...form, notasInternas: e.target.value })}
                  className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-sm"
                  placeholder="Visible solo para el personal (opcional)" />
              </div>

              {/* Toggle confirmar inmediatamente */}
              <label className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg cursor-pointer">
                <div
                  onClick={() => setForm(f => ({ ...f, confirmarInmediatamente: !f.confirmarInmediatamente }))}
                  className={`w-10 h-5 rounded-full transition-colors relative shrink-0 ${form.confirmarInmediatamente ? "bg-emerald-500" : "bg-slate-300"}`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.confirmarInmediatamente ? "translate-x-5" : "translate-x-0"}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-800">Confirmar inmediatamente</p>
                  <p className="text-xs text-emerald-600">La reserva queda confirmada al crearla, sin esperar al cliente.</p>
                </div>
              </label>

              <button onClick={crearReserva} disabled={guardando}
                className="w-full py-2.5 bg-brand-600 text-white rounded-md text-sm font-medium hover:bg-brand-700 disabled:opacity-40">
                {guardando ? "Creando…" : form.confirmarInmediatamente ? "Crear y confirmar reserva" : "Crear reserva"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
